"use client";

import React, { useEffect } from "react";
import {
  Handle,
  Position,
  useUpdateNodeInternals,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import { ShieldCheck, StickyNote } from "lucide-react";
import type { IdeaNodeData } from "./types";
import { EXECUTION_GATE_HANDLE_ID, NODE_META } from "./constants";

export function IdeaFlowNode({
  id,
  data,
  selected,
}: NodeProps<Node<IdeaNodeData>>) {
  const updateNodeInternals = useUpdateNodeInternals();
  const m = NODE_META[data.nodeKind];
  const Icon = m?.icon || null;
  const allPorts = data.ports ?? [];
  const inputPorts = allPorts.filter((p) => p.direction === "input");
  const outputPorts = allPorts.filter((p) => p.direction === "output");
  const hasPortDefs = data.nodeKind === "action";

  const portKey = allPorts.map((p) => p.id + p.direction).join(",");

  useEffect(() => {
    if (hasPortDefs) {
      updateNodeInternals(id);
    }
  }, [portKey, id, hasPortDefs, updateNodeInternals]);

  if (!m) {
    return (
      <div className="rounded-2xl border border-red-500 bg-red-50 p-3 dark:bg-red-500/10">
        <p className="text-xs font-semibold text-red-600">Invalid Node Type</p>
      </div>
    );
  }

  const baseHandleStyle = {
    background: m.handleColor,
    width: 10,
    height: 10,
    border: "2px solid white",
  };

  return (
    <div
      className={`relative rounded-2xl border bg-white shadow-sm transition-all dark:bg-zinc-900 ${
        selected ? `ring-2 ${m.ring} shadow-md` : m.border
      } min-w-52 max-w-72`}
    >
      {/* Header */}
      <div
        className={`flex items-center gap-2 rounded-t-2xl px-3 py-2 ${m.bg}`}
      >
        <Icon className={`h-3.5 w-3.5 shrink-0 ${m.color}`} />
        <span
          className={`text-[10px] font-bold uppercase tracking-wider ${m.color}`}
        >
          {data.actionPayload?.subtype === "llm_prompt"
            ? "LLM · Gemini"
            : m.label}
        </span>
        {(data.notes?.length ?? 0) > 0 && (
          <span className="ml-auto flex items-center gap-0.5 text-[10px] text-amber-400">
            <StickyNote className="h-2.5 w-2.5" />
            {data.notes.length}
          </span>
        )}
      </div>

      {/* Title / description */}
      <div className="px-3 py-2.5">
        <p className="truncate text-sm font-semibold text-black dark:text-white">
          {data.title || (
            <span className="font-normal italic text-gray-300">kein Titel</span>
          )}
        </p>
        {data.description && (
          <p className="mt-0.5 line-clamp-2 text-[11px] text-gray-500">
            {data.description}
          </p>
        )}
        {data.signal && (
          <p className="mt-2 text-[11px] text-sky-500">
            {data.signal.event || "Ereignis offen"}
            <br />
            <span className="text-gray-500">{data.signal.source}</span>
          </p>
        )}
        {data.state && (
          <p className="mt-2 text-[11px] text-violet-500">
            {data.state.value || "Zustand offen"}
            <br />
            <span className="text-gray-500">{data.state.enterWhen}</span>
          </p>
        )}
        {!!data.runs?.length && (
          <p className="mt-2 text-[10px] text-gray-500">
            Letzter Test:{" "}
            {data.runs[0].status === "success"
              ? "erfolgreich"
              : data.runs[0].status === "cancelled"
                ? "abgebrochen"
                : "Fehler"}
          </p>
        )}
      </div>

      {/* Port rows for action / trigger */}
      {hasPortDefs && (
        <div className="border-t border-gray-100 dark:border-white/5">
          {allPorts.length === 0 ? (
            <p className="px-3 py-2 text-[10px] italic text-gray-300">
              Keine Ports – im Panel hinzufügen
            </p>
          ) : (
            <div className="flex justify-between gap-2 px-4 py-1.5">
              {/* Input ports — left column */}
              <div className="flex flex-col gap-1">
                {inputPorts.map((p) => (
                  <div
                    key={p.id}
                    className="relative flex h-6 items-center gap-1"
                  >
                    <Handle
                      type="target"
                      position={Position.Left}
                      id={p.id}
                      style={{ ...baseHandleStyle, left: "-17px" }}
                    />
                    <span className="max-w-24 truncate text-[10px] text-gray-500">
                      {p.name || (
                        <span className="italic text-gray-300">in</span>
                      )}
                    </span>
                    {p.primitiveType && (
                      <span className="font-mono text-[9px] text-gray-400">
                        {p.primitiveType}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              {/* Output ports — right column */}
              <div className="flex flex-col items-end gap-1">
                {outputPorts.map((p) => (
                  <div
                    key={p.id}
                    className="relative flex h-6 items-center justify-end gap-1"
                  >
                    {p.primitiveType && (
                      <span className="font-mono text-[9px] text-gray-400">
                        {p.primitiveType}
                      </span>
                    )}
                    <span className="max-w-24 truncate text-right text-[10px] text-gray-500">
                      {p.name || (
                        <span className="italic text-gray-300">out</span>
                      )}
                    </span>
                    <Handle
                      type="source"
                      position={Position.Right}
                      id={p.id}
                      style={{ ...baseHandleStyle, right: "-17px" }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Execution Gate — always present on action nodes */}
      {hasPortDefs && (
        <div className="border-t border-emerald-500/20 bg-emerald-500/5 px-4 py-1.5">
          <div className="relative flex h-6 items-center gap-1.5">
            <Handle
              type="target"
              position={Position.Left}
              id={EXECUTION_GATE_HANDLE_ID}
              style={{
                ...baseHandleStyle,
                background: "#10b981",
                left: "-17px",
              }}
            />
            <ShieldCheck className="h-3 w-3 shrink-0 text-emerald-500" />
            <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
              Darf ausführen
            </span>
          </div>
        </div>
      )}

      {/* Global Handles only for non-action nodes */}
      {!hasPortDefs && (
        <>
          <Handle
            type="target"
            position={Position.Left}
            style={baseHandleStyle}
          />
          <Handle
            type="source"
            position={Position.Right}
            style={baseHandleStyle}
          />
        </>
      )}
      {/* When action node has no output ports, keep a generic source handle */}
      {hasPortDefs && inputPorts.length === 0 && (
        <Handle
          type="target"
          position={Position.Left}
          style={baseHandleStyle}
        />
      )}
      {hasPortDefs && outputPorts.length === 0 && (
        <Handle
          type="source"
          position={Position.Right}
          style={baseHandleStyle}
        />
      )}
    </div>
  );
}

export const nodeTypes = { ideaNode: IdeaFlowNode };
